import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useInvalidate, useTranslate } from "@refinedev/core";
import { AlertTriangle, Printer, Utensils } from "lucide-react";
import { DateField, Button, Field, Modal } from "@fawkes/ui";

import { Werklijst, WeergaveKeuze, useWeergave, type Kolom, type Rij } from "../components/WerklijstWeergave";
import { PensionLijstFilters, PensionLijstKop, PensionLijstMeta, PensionLijstTegel, zoekIn } from "../components/PensionLijstKop";
import { printVoerstickers } from "../components/printVoerstickers";
import { todayIso } from "../i18n/format";
import "./PensionWerklijst.css";

type Staat = "GEPLAND" | "ACHTERSTALLIG" | "GEVOERD" | "GEEN_PLAN";
type Dagdeel = "OCHTEND" | "MIDDAG" | "AVOND";

/** Het eetgedrag zoals het dierdossier het kent; de lijst gebruikt dezelfde codes. */
const GEDRAG = ["ALLES_GEGETEN", "BIJNA_OP", "GOED_GEGETEN", "SLECHT_GEGETEN", "ONBEKEND"] as const;

interface Regel {
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  klantId: number | null;
  klantNaam: string | null;
  kennel: string | null;
  voer: string | null;
  hoeveelheid: string | null;
  dieet: string | null;
  notitie: string | null;
  voertijdenTekst: string | null;
  gramUitPlan: number | null;
  tijd: string | null;
  dagdeel: Dagdeel | null;
  door: string | null;
  gedrag: string | null;
  staat: Staat;
}

interface VoerlijstData {
  vestigingId: number;
  datum: string;
  telling: { teVoeren: number; gevoerd: number; achterstallig: number; zonderPlan: number };
  regels: Regel[];
}

/** Het dagdeelfilter. "Alle" staat voorop; de wens vraagt om "alleen de middag". */
type Deel = "ALLE" | Dagdeel;

/**
 * Het overzicht "Voerlijst" — wens §5 uit de wensenlijst.
 *
 * <p>Eén regel per <b>voermoment</b>: een hond die twee keer per dag eet staat er twee keer op. Het
 * <b>dagdeelfilter</b> is waar de wens om vraagt — wie de middagronde loopt wil niet langs de
 * ochtendbeurten scrollen. De <b>sticker</b> blijft: aan de voerkeuken staat niemand met een scherm,
 * en wat een dier krijgt hoort op de bak te staan.
 *
 * <p>Afvinken schrijft een gewone regel in de voerlog, dezelfde die het dierdossier toont, met de
 * initialen van wie voerde erbij. Noemt het plan geen hoeveelheid, dan vraagt het scherm er één —
 * de voerlog eist een aantal gram, en een verzonnen nul zou daar lezen als "nul gram gegeven".
 */
export function PensionVoerlijst({ onOpenDier }: { onOpenDier?: (dierId: number) => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const invalidate = useInvalidate();
  const [datum, setDatum] = useState(() => todayIso());
  const [deel, setDeel] = useState<Deel>("ALLE");
  const [zoek, setZoek] = useState("");
  const [weergave, setWeergave] = useWeergave("voerlijst");
  const [initialen, setInitialen] = useState("");
  const [gedrag, setGedrag] = useState<string>("ALLES_GEGETEN");
  const [gramVoor, setGramVoor] = useState<Regel | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const query = useMemo(() => ({ datum }), [datum]);
  const { data, isLoading, isError, refetch } = useCustom<VoerlijstData>({
    url: "pension/voerlijst",
    method: "get",
    config: { query },
  });
  const { mutate, isLoading: bezig } = useCustomMutation();

  const voerlijst = data?.data;
  const alle = voerlijst?.regels ?? [];
  const regels = zoekIn(deel === "ALLE" ? alle : alle.filter((r) => r.dagdeel === deel), zoek);

  const aantalPerDeel = (d: Deel) =>
    d === "ALLE" ? alle.length : alle.filter((r) => r.dagdeel === d).length;

  const sleutel = (r: Regel) => `${r.dierId}-${r.tijd ?? "?"}`;

  function tekenAf(regel: Regel, gram?: number) {
    setFout(null);
    mutate(
      {
        url: "pension/voerlijst",
        method: "post",
        values: {
          dierId: regel.dierId,
          tijd: regel.tijd,
          initialen: initialen.trim() || null,
          gedrag,
          gram: gram ?? null,
        },
      },
      {
        onSuccess: () => {
          setGramVoor(null);
          refetch();
          // Het dierdossier toont dezelfde voerlog; anders staat daar straks een oude stand.
          invalidate({ resource: "animals", invalidates: ["all"] });
        },
        onError: () => setFout(sleutel(regel)),
      },
    );
  }

  /** Eén sticker per dier — niet per moment, want er gaat één bak per dier de keuken uit. */
  function stickers() {
    const perDier = new Map<number, Regel>();
    for (const r of alle) {
      if (!perDier.has(r.dierId)) perDier.set(r.dierId, r);
    }
    printVoerstickers({
      organisatie: t("pensionVoerlijst.title"),
      datum: new Date(datum).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      labels: {
        titel: t("pensionVoerlijst.sticker.titel"),
        voer: t("pensionVoerlijst.kolom.voer"),
        hoeveelheid: t("pensionVoerlijst.kolom.hoeveelheid"),
        tijden: t("pensionVoerlijst.sticker.tijden"),
        dieet: t("pensionVoerlijst.sticker.dieet"),
        notitie: t("pensionVoerlijst.sticker.notitie"),
      },
      stickers: [...perDier.values()].map((r) => ({
        dierNaam: r.dierNaam ?? "—",
        kennel: r.kennel,
        voer: r.voer,
        hoeveelheid: r.hoeveelheid,
        voertijden: r.voertijdenTekst,
        dieet: r.dieet,
        notitie: r.notitie,
      })),
    });
  }

  // Tabel en tegels komen uit dezelfde kolommen (WerklijstWeergave).
  const kolommen: Kolom<(typeof regels)[number]>[] = [
    {
      kop: t("pensionVoerlijst.kolom.tijd"),
      cel: (r) => <>{r.tijd ?? "—"}</>,
      klasse: "pension-werklijst__getal",
    },
    {
      kop: t("pensionVoerlijst.kolom.dier"),
      cel: (r) => (
        <>
          {onOpenDier ? (
            <button
              type="button"
              className="pension-werklijst__link"
              onClick={() => onOpenDier(r.dierId)}
            >
              {r.dierNaam ?? "—"}
            </button>
          ) : (
            (r.dierNaam ?? "—")
          )}
          {r.diersoort && <span className="pension-chip">{r.diersoort}</span>}
        </>
      ),
      rol: "dier",
    },
    {
      kop: t("pensionVoerlijst.kolom.kennel"),
      cel: (r) => <>{r.kennel ?? "—"}</>,
      rol: "verblijf",
    },
    {
      kop: t("pensionVoerlijst.kolom.voer"),
      cel: (r) => (
        <>
          {r.voer ?? "—"}
          {r.dieet && <span className="pension-werklijst__door"> · {r.dieet}</span>}
        </>
      ),
    },
    {
      kop: t("pensionVoerlijst.kolom.hoeveelheid"),
      cel: (r) => <>{r.hoeveelheid ?? "—"}</>,
    },
    {
      kop: t("pensionVoerlijst.kolom.staat"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__reden" data-reden={r.staat}>
            {r.staat === "ACHTERSTALLIG" && <AlertTriangle size={13} aria-hidden="true" />}
            {t(`pensionVoerlijst.staat.${r.staat}`)}
          </span>
          {r.staat === "GEVOERD" && r.door && (
            <span className="pension-werklijst__door"> · {r.door}</span>
          )}
        </>
      ),
    },
    {
      kop: t("pensionVoerlijst.kolom.afvinken"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__invoer">
            {r.staat === "GEVOERD" ? (
              <span className="fawkes-muted">{t("pensionVoerlijst.klaar")}</span>
            ) : r.staat === "GEEN_PLAN" ? (
              <span className="fawkes-muted">{t("pensionVoerlijst.geenPlan")}</span>
            ) : (
              <Button
                variant="primary"
                disabled={bezig}
                onClick={() =>
                  r.gramUitPlan == null ? setGramVoor(r) : tekenAf(r)
                }
              >
                {t("pensionVoerlijst.gevoerd")}
              </Button>
            )}
            {fout === sleutel(r) && (
              <span className="fawkes-error" role="status">
                {t("pensionVoerlijst.mislukt")}
              </span>
            )}
          </span>
        </>
      ),
      rol: "actie",
    },
  ];
  const rij = (r: (typeof regels)[number]): Rij => ({ key: sleutel(r), "data-signaal": r.staat === "ACHTERSTALLIG" ? "ACTIE_NODIG" : "OK", "data-tewegen": r.staat === "GEPLAND" || r.staat === "ACHTERSTALLIG" ? "" : undefined });

  return (
    <div className="pension-werklijst fawkes-pgo">
      <PensionLijstKop icoon={<Utensils size={22} />} titel={t("pensionVoerlijst.title")} subtitel={t("pensionVoerlijst.subtitle")}>
          <PensionLijstTegel waarde={voerlijst?.telling.teVoeren} label={t("pensionVoerlijst.teller.teVoeren")} />
          <PensionLijstTegel waarde={voerlijst?.telling.gevoerd} label={t("pensionVoerlijst.teller.gevoerd")} />
          <PensionLijstTegel
            waarde={voerlijst?.telling.achterstallig}
            label={t("pensionVoerlijst.teller.achterstallig")}
            toon={voerlijst && voerlijst.telling.achterstallig > 0 ? "rood" : undefined}
          />
          <PensionLijstTegel waarde={voerlijst?.telling.zonderPlan} label={t("pensionVoerlijst.teller.zonderPlan")} />
      </PensionLijstKop>

      <PensionLijstFilters
        zoek={zoek}
        onZoek={setZoek}
        weergave={<WeergaveKeuze waarde={weergave} onKies={setWeergave} />}
        snelLabel={t("pensionVoerlijst.dagdeel")}
        rechts={
          <div className="pension-werklijst__balk">
            <input
              type="text"
              className="pension-werklijst__initialen"
              value={initialen}
              maxLength={10}
              placeholder={t("pensionVoerlijst.initialen")}
              aria-label={t("pensionVoerlijst.initialen")}
              onChange={(e) => setInitialen(e.target.value)}
            />
            <select
              className="pension-werklijst__keuze"
              value={gedrag}
              aria-label={t("pensionVoerlijst.gedrag")}
              onChange={(e) => setGedrag(e.target.value)}
            >
              {GEDRAG.map((code) => (
                <option key={code} value={code}>
                  {t(`animalDetailView.feeding.behavior.${code}`)}
                </option>
              ))}
            </select>
            <label className="po-veld werklijst-datum">
              <span>{t("pensionOverzichten.datum")}</span>
              <DateField className="pension-werklijst__datum"
                value={datum}
                onChange={(e) => e.target.value && setDatum(e.target.value)}
                aria-label={t("pensionVoerlijst.datum")}
              />
            </label>
            <Button variant="secondary" onClick={() => setDatum(todayIso())}>
              {t("pensionVoerlijst.vandaag")}
            </Button>
            <Button variant="secondary" disabled={alle.length === 0} onClick={stickers}>
              <Printer size={17} aria-hidden="true" /> {t("pensionVoerlijst.stickers")}
            </Button>
          </div>
        }
        snelfilters={
          <div className="pension-werklijst__soorten" role="group" aria-label={t("pensionVoerlijst.dagdeel")}>
            {(["ALLE", "OCHTEND", "MIDDAG", "AVOND"] as Deel[]).map((d) => (
              <button
                key={d}
                type="button"
                className="pension-werklijst__soort"
                data-actief={deel === d ? "" : undefined}
                aria-pressed={deel === d}
                onClick={() => setDeel(d)}
              >
                {t(`pensionVoerlijst.dagdeel.${d}`)}
                <span className="pension-werklijst__soortaantal">{aantalPerDeel(d)}</span>
              </button>
            ))}
          </div>
        }
      />

      {isError ? (
        <p className="fawkes-muted">{t("pensionVoerlijst.loadFailed")}</p>
      ) : (
        <>
          <PensionLijstMeta aantal={regels.length} />
          {isLoading ? (
            <p className="pension-werklijst__leeg">{t("common.loading")}</p>
          ) : regels.length === 0 ? (
            <p className="pension-werklijst__leeg">
              <Utensils size={26} aria-hidden="true" />
              {t("pensionVoerlijst.leeg")}
            </p>
          ) : (
            <Werklijst lijst="voerlijst" regels={regels} kolommen={kolommen} weergave={weergave} rij={rij} dierId={(r) => r.dierId} />
          )}
        </>
      )}

      {gramVoor && (
        <GramDialoog
          regel={gramVoor}
          bezig={bezig}
          onClose={() => setGramVoor(null)}
          onOpslaan={(gram) => tekenAf(gramVoor, gram)}
        />
      )}
    </div>
  );
}


/**
 * Hoeveel gram er in de bak ging. Komt alleen als het plan geen getal noemt — de voerlog eist er een,
 * en een verzonnen nul leest in het dierdossier als "nul gram gegeven".
 */
function GramDialoog({
  regel,
  bezig,
  onClose,
  onOpslaan,
}: {
  regel: Regel;
  bezig: boolean;
  onClose: () => void;
  onOpslaan: (gram: number) => void;
}) {
  const t = useTranslate();
  const [waarde, setWaarde] = useState("");
  const gram = Number(waarde.replace(",", "."));
  const geldig = waarde.trim() !== "" && Number.isFinite(gram) && gram >= 1;

  return (
    <Modal
      open
      title={t("pensionVoerlijst.gram.titel", { args: [regel.dierNaam ?? ""] })}
      onClose={onClose}
    >
      <p className="fawkes-muted">
        {t("pensionVoerlijst.gram.uitleg", { args: [regel.hoeveelheid ?? "—"] })}
      </p>
      <Field
        label={t("pensionVoerlijst.gram.veld")}
        value={waarde}
        onChange={(e) => setWaarde(e.target.value)}
      />
      <div className="pension-werklijst__dialoogknoppen">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" disabled={bezig || !geldig} onClick={() => onOpslaan(Math.round(gram))}>
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}
